import { useEffect, useState } from "react";
import {
  Box, Paper, Typography, Button, Chip, Grid, Divider, Stack, Tooltip, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, IconButton, Snackbar, Alert, CircularProgress
} from "@mui/material";
import AddIcon from "@mui/icons-material/PersonAdd";
import DeleteIcon from "@mui/icons-material/Delete";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import LockResetIcon from "@mui/icons-material/LockReset";
import { listUsers, createUser, deleteUser, getProfile, adminResetPassword, resetMyPassword } from "../api/users";
import { getEquipments } from "../api/equipment";

export default function Usuarios() {
  const [me, setMe] = useState(null);
  const [users, setUsers] = useState([]);
  const [equipamentos, setEquipamentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openAdd, setOpenAdd] = useState(false);
  const [openPwd, setOpenPwd] = useState(false);
  const [openAdminPwd, setOpenAdminPwd] = useState({ open: false, user: null });
  const [pwdForm, setPwdForm] = useState({ current: "", new: "", confirm: "" });
  const [adminPwd, setAdminPwd] = useState({ new: "", confirm: "" });
  const [newUser, setNewUser] = useState({ username: "", email: "", password: "", is_admin: false });
  const [msg, setMsg] = useState({ open: false, text: "", severity: "success" });
  const [deleting, setDeleting] = useState("");
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    setLoading(true);
    getProfile().then(res => {
      setMe(res.data);
      setLoading(false);
      if (res.data.is_admin) listUsers().then(r => setUsers(r.data));
    });
    getEquipments().then(setEquipamentos).catch(() => {});
  }, [refresh]);

  const isAdmin = !!me?.is_admin;

  const handleAddUser = async () => {
    if (!newUser.username || !newUser.email || !newUser.password) {
      setMsg({ open: true, text: "Preencha todos os campos.", severity: "warning" });
      return;
    }
    try {
      await createUser(newUser);
      setMsg({ open: true, text: "Usuário adicionado!", severity: "success" });
      setOpenAdd(false);
      setNewUser({ username: "", email: "", password: "", is_admin: false });
      setRefresh(r => r + 1);
    } catch (err) {
      setMsg({ open: true, text: err.response?.data?.error || "Erro ao adicionar.", severity: "error" });
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Tem certeza que deseja excluir este usuário?")) {
      setDeleting(id);
      try {
        await deleteUser(id);
        setMsg({ open: true, text: "Usuário excluído.", severity: "success" });
        setRefresh(r => r + 1);
      } catch (err) {
        setMsg({ open: true, text: err.response?.data?.error || "Erro ao excluir.", severity: "error" });
      }
      setDeleting("");
    }
  };

  async function handleRedefinirSenha() {
    if (pwdForm.new !== pwdForm.confirm) {
      setMsg({ open: true, text: "Senhas não coincidem.", severity: "error" });
      return;
    }
    try {
      await resetMyPassword(pwdForm.current, pwdForm.new);
      setOpenPwd(false);
      setPwdForm({ current: "", new: "", confirm: "" });
      setMsg({ open: true, text: "Senha redefinida com sucesso!", severity: "success" });
    } catch (err) {
      setMsg({ open: true, text: err.response?.data?.error || "Erro ao redefinir senha.", severity: "error" });
    }
  }

  async function handleAdminRedefinirSenha() {
    if (adminPwd.new !== adminPwd.confirm) {
      setMsg({ open: true, text: "Senhas não coincidem.", severity: "error" });
      return;
    }
    try {
      await adminResetPassword(openAdminPwd.user.id, adminPwd.new);
      setOpenAdminPwd({ open: false, user: null });
      setAdminPwd({ new: "", confirm: "" });
      setMsg({ open: true, text: "Senha redefinida com sucesso!", severity: "success" });
    } catch (err) {
      setMsg({ open: true, text: err.response?.data?.error || "Erro ao redefinir senha.", severity: "error" });
    }
  }

  function handleLogout() {
    localStorage.removeItem("token");
    window.location.href = "/login";
  }

  if (loading) return (
    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", mt: 8 }}>
      <CircularProgress size={42} />
    </Box>
  );
  if (!me) return null;

  // --- Painel ADMIN ---
  if (isAdmin) {
    return (
      <Box sx={{ maxWidth: 800, mx: "auto", mt: 3 }}>
        <Paper elevation={3} sx={{ p: 3, borderRadius: 3 }}>
          <Grid container alignItems="center" spacing={2} sx={{ mb: 1 }}>
            <Grid item>
              <AccountCircleIcon sx={{ fontSize: 55, color: "#1976d2" }} />
            </Grid>
            <Grid item xs>
              <Typography variant="h5" fontWeight={600}>Gerenciamento de Usuários</Typography>
            </Grid>
            <Grid item>
              <Stack direction="row" spacing={1}>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpenAdd(true)}>
                  Novo Usuário
                </Button>
                <Button variant="outlined" color="error" onClick={handleLogout}>
                  Logout
                </Button>
              </Stack>
            </Grid>
          </Grid>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1" fontWeight={600} mb={1}>Usuários cadastrados:</Typography>
          <Stack spacing={1}>
            {users.map(u => (
              <Paper
                key={u.id}
                sx={{
                  p: 2,
                  display: "flex",
                  alignItems: "center",
                  bgcolor: u.id === me.id ? "action.selected" : "background.paper",
                  borderRadius: 2,
                  boxShadow: 0,
                  flexWrap: "wrap",
                  mb: 0.5
                }}
              >
                <Box sx={{ flexGrow: 1, minWidth: 170 }}>
                  <Typography fontWeight={600}>{u.username}
                    {u.is_admin && <Chip label="Admin" color="primary" size="small" sx={{ ml: 1 }} />}
                    {me && u.id === me.id && <Chip label="Você" color="success" size="small" sx={{ ml: 1 }} />}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">{u.email}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Último login: {u.last_login ? new Date(u.last_login).toLocaleString("pt-BR") : "-"}
                    {u.last_ip && <> | IP: {u.last_ip}</>}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={0.5} sx={{ minWidth: 100 }}>
                  {u.id !== me?.id && (
                    <>
                      <Tooltip title="Redefinir senha">
                        <span>
                          <IconButton color="info" onClick={() => setOpenAdminPwd({ open: true, user: u })} size="small">
                            <LockResetIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Excluir usuário">
                        <span>
                          <IconButton color="error" onClick={() => handleDelete(u.id)} size="small" disabled={deleting === u.id}>
                            {deleting === u.id ? <CircularProgress size={20} /> : <DeleteIcon />}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </>
                  )}
                </Stack>
              </Paper>
            ))}
          </Stack>
        </Paper>
        {/* Modal adicionar usuário */}
        <Dialog open={openAdd} onClose={() => setOpenAdd(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Novo Usuário</DialogTitle>
          <DialogContent sx={{ pb: 0 }}>
            <TextField
              autoFocus margin="dense" label="Nome de usuário" fullWidth
              value={newUser.username} onChange={e => setNewUser({ ...newUser, username: e.target.value })}
            />
            <TextField
              margin="dense" label="E-mail" fullWidth type="email"
              value={newUser.email} onChange={e => setNewUser({ ...newUser, email: e.target.value })}
            />
            <TextField
              margin="dense" label="Senha" fullWidth type="password"
              value={newUser.password} onChange={e => setNewUser({ ...newUser, password: e.target.value })}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenAdd(false)}>Cancelar</Button>
            <Button onClick={handleAddUser} variant="contained">Adicionar</Button>
          </DialogActions>
        </Dialog>
        {/* Modal redefinir senha ADMIN */}
        <Dialog open={openAdminPwd.open} onClose={() => setOpenAdminPwd({ open: false, user: null })} maxWidth="xs" fullWidth>
          <DialogTitle>Redefinir senha para {openAdminPwd.user?.username}</DialogTitle>
          <DialogContent>
            <TextField
              margin="dense" label="Nova Senha" fullWidth type="password"
              value={adminPwd.new} onChange={e => setAdminPwd(f => ({ ...f, new: e.target.value }))}
            />
            <TextField
              margin="dense" label="Confirmar Nova Senha" fullWidth type="password"
              value={adminPwd.confirm} onChange={e => setAdminPwd(f => ({ ...f, confirm: e.target.value }))}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenAdminPwd({ open: false, user: null })}>Cancelar</Button>
            <Button onClick={handleAdminRedefinirSenha} variant="contained">Salvar</Button>
          </DialogActions>
        </Dialog>
        <Snackbar open={msg.open} autoHideDuration={3000} onClose={() => setMsg({ ...msg, open: false })}>
          <Alert severity={msg.severity}>{msg.text}</Alert>
        </Snackbar>
      </Box>
    );
  }

  // --- Painel USUÁRIO COMUM ---
  return (
    <Box sx={{ maxWidth: 650, mx: "auto", mt: 3 }}>
      <Paper elevation={3} sx={{ p: 3, borderRadius: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item>
            <AccountCircleIcon sx={{ fontSize: 70, color: "#1976d2" }} />
          </Grid>
          <Grid item xs>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>{me.username}
              <Chip label="Usuário" color="primary" size="small" sx={{ ml: 2 }} />
            </Typography>
            <Typography variant="subtitle1" color="textSecondary">{me.email}</Typography>
          </Grid>
          <Grid item>
            <Button variant="outlined" color="error" onClick={handleLogout}>Logout</Button>
          </Grid>
        </Grid>
        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
          Equipamentos com acesso:
        </Typography>
        <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", mb: 2 }}>
          {equipamentos.map((eq, i) => (
            <Chip key={i} label={`${eq.location} (${eq.mac})`} color="info" size="small" sx={{ mb: 1 }} />
          ))}
        </Stack>
        <Button variant="contained" sx={{ mt: 2 }} onClick={() => setOpenPwd(true)}>
          Redefinir Senha
        </Button>
        {/* Modal redefinir senha */}
        <Dialog open={openPwd} onClose={() => setOpenPwd(false)}>
          <DialogTitle>Redefinir Senha</DialogTitle>
          <DialogContent>
            <TextField label="Senha Atual" fullWidth type="password"
              sx={{ mb: 2 }} value={pwdForm.current} onChange={e => setPwdForm(f => ({ ...f, current: e.target.value }))} />
            <TextField label="Nova Senha" fullWidth type="password"
              sx={{ mb: 2 }} value={pwdForm.new} onChange={e => setPwdForm(f => ({ ...f, new: e.target.value }))} />
            <TextField label="Confirmar Nova Senha" fullWidth type="password"
              value={pwdForm.confirm} onChange={e => setPwdForm(f => ({ ...f, confirm: e.target.value }))} />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenPwd(false)}>Cancelar</Button>
            <Button onClick={handleRedefinirSenha} variant="contained">Salvar</Button>
          </DialogActions>
        </Dialog>
        <Snackbar open={msg.open} autoHideDuration={3000} onClose={() => setMsg({ ...msg, open: false })}>
          <Alert severity={msg.severity}>{msg.text}</Alert>
        </Snackbar>
      </Paper>
    </Box>
  );
}

